import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideCtaBand from "../components/GuideCtaBand";
import TollLaneAnimation from "../components/TollLaneAnimation";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "french-toll-roads")!;

// Section accents pulled from the brand --gt-feature-* palette, same
// scheme as the nav guides and packing guides.
const ACCENT = {
    teal: { fill: "#0f4c5c", chipBg: "rgba(15,76,92,.12)", chipText: "#0f4c5c" },
    rust: { fill: "#c1654a", chipBg: "rgba(193,101,74,.13)", chipText: "#a1462c" },
    slate: { fill: "#5a6772", chipBg: "rgba(90,103,114,.13)", chipText: "#485460" },
    green: { fill: "#5b8a72", chipBg: "rgba(91,138,114,.14)", chipText: "#3f6b53" },
    plum: { fill: "#7a4f6d", chipBg: "rgba(122,79,109,.14)", chipText: "#5f3c54" },
} as const;

type Item = { title: string; body: React.ReactNode };
type Section = {
    catnum: string;
    chipLabel: string;
    accent: keyof typeof ACCENT;
    heading: string;
    intro?: string;
    items: Item[];
};

const SECTIONS: Section[] = [
    {
        catnum: "01",
        chipLabel: "Start here",
        accent: "teal",
        heading: "How the French toll system works",
        intro:
            "France's autoroute network (A-roads) is the fastest way across the country and one of the most expensive per kilometre in Europe. The good news: motorbikes pay roughly 60% of car rates, and there are several ways to avoid the queues.",
        items: [
            {
                title: "Concessions, not the state",
                body:
                    "The autoroutes are run by private concessions — Vinci (ASF, Cofiroute, Escota), Sanef, APRR/AREA, and a handful of smaller operators. That's why prices and the look of the tollbooths change as you move across the country. Free national roads (N-roads) and departmental roads (D-roads) are still maintained by the state and cost nothing to ride on.",
            },
            {
                title: "Open vs. closed systems",
                body:
                    "Most autoroutes use a closed system: you take a ticket at entry and pay at exit based on distance travelled. Some shorter sections — particularly around city ring roads and the A14 west of Paris — use an open system with a flat fee at a single barrier. The signage is the same either way; just follow the péage symbols.",
            },
            {
                title: "Motorbikes pay Class 5",
                body:
                    "French tolls have five vehicle classes. Motorbikes are Class 5 — the cheapest. Expect roughly €0.06–€0.10 per kilometre. Calais to Paris is about €20–€25, Calais to Nice all-autoroute is around €110. Bring a card and don't rely on cash — manned booths are disappearing.",
            },
        ],
    },
    {
        catnum: "02",
        chipLabel: "The game-changer",
        accent: "rust",
        heading: "Liber-t tags — the game-changer for regular visitors",
        intro:
            "A small electronic tag on the bike that opens orange-lane barriers automatically. It's the single biggest quality-of-life upgrade for any rider who crosses the Channel more than once a year.",
        items: [
            {
                title: "Bip&Go and Fulli — the two UK-facing operators",
                body:
                    "Both let UK residents sign up online without a French bank account. You order a moto-specific tag, fix it to the inside of the windscreen or on the upper fork with the supplied cradle, and you're set. Tags arrive in 5–7 working days by post.",
            },
            {
                title: "Cost",
                body:
                    "Around €1.70–€2.50 per month subscription per tag (a few operators waive months you don't use), plus the actual tolls at the standard Class 5 rate. No per-transaction surcharge. Worth it if you'll pass through 4+ toll plazas in a year.",
            },
            {
                title: "Why it's worth it",
                body:
                    "The orange lane is almost always empty in summer. You roll up to the barrier at walking pace, the gantry beeps, the barrier lifts. No glove off, no card insert, no waiting behind a coach. Tolls are itemised on a monthly statement, which is useful if you're claiming back expenses.",
            },
            {
                title: "Works for cars too",
                body:
                    "If you also have a car you take to France, the same tag works in the car if you move the cradle over. Cheaper than running two accounts.",
            },
            {
                title: "Tag placement on a bike",
                body:
                    "Orientation matters more than location. In a jacket pocket or armband, the front face of the badge (the side with the barcode or logo) goes inward against your body, or downward — never pointing outward. Mounted on the bike (windscreen or tank bag), place the tag with the barcode/label facing the ground or down against the bike's surface, with no metal between tag and gantry. Test it on the way out at the first barrier — if it doesn't beep, the green-arrow lane is your friend.",
            },
        ],
    },
    {
        catnum: "03",
        chipLabel: "The new barrier-less system",
        accent: "slate",
        heading: "Free flow (Flux Libre) — the new barrier-less system",
        intro:
            "France has started replacing tollbooths with overhead camera gantries on some autoroutes. There's nothing to stop for and nothing to pay at the time. This catches UK riders out because there's no obvious indication you've been charged.",
        items: [
            {
                title: "Where it's in use",
                body:
                    "The A79 in central France, the A13/A14 ring around Paris, sections of the A4. More routes are being converted every year. Look for blue 'Péage Flux Libre' signs about a kilometre before each gantry.",
            },
            {
                title: "How it works",
                body:
                    "Cameras read your number plate and the tag if you have one. If you have a Liber-t/Bip&Go/Fulli tag, the charge appears on your next statement automatically — nothing else to do.",
            },
            {
                title: "If you don't have a tag",
                body: (
                    <>
                        You have 72 hours from passing under the gantry to pay
                        online at the operator's website —{" "}
                        <a
                            href="https://www.aliae.com/free-flow-tolls"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            aliae.com
                        </a>{" "}
                        for the A79,{" "}
                        <a
                            href="https://www.autoroutes.sanef.com/en/assistance/free-flow/how-to-pay"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            sanef.com
                        </a>{" "}
                        for the A13/A14. You'll need your number plate and a
                        bank card. Both operators also take payment at
                        tobacconists on the Nirio network, and the A79 has
                        payment machines at every service area.
                    </>
                ),
            },
            {
                title: "If you forget to pay",
                body:
                    "After 72 hours the fee starts increasing. After 60 days it becomes a formal fine. France can chase UK plates via the EU's cross-border enforcement framework — it's no longer the free pass it used to be. Easiest fix: get a Liber-t tag and never think about it.",
            },
        ],
    },
    {
        catnum: "04",
        chipLabel: "The actual riding bit",
        accent: "green",
        heading: "At the plaza — the actual riding bit",
        items: [
            {
                title: "Slow down at the first sign",
                body:
                    "The 1km warning sign is your cue to drop two gears and start looking at the lane signage above the plaza. Most plazas have a clear lane-counter display visible from about 400m out — pick a lane there, not at the barrier.",
            },
            {
                title: "Have payment ready before the lane",
                body:
                    "Card in your tank-bag pocket or jacket sleeve before you stop. Fumbling for a wallet in a glove while the queue behind you grows is everyone's least favourite ten seconds.",
            },
            {
                title: "Lane filtering at the approach",
                body:
                    "Filtering is legal at French tolls — but be cautious. Many of the bikes ahead are paying differently to you, so you can't assume they'll move at the same speed. Don't filter past traffic into the orange lane unless you have a tag.",
            },
            {
                title: "Visor up if you need to speak",
                body:
                    "Even in the green lane the booth attendant often hands the receipt up to you. Pop the visor before you stop so you can talk and grab the slip without fumbling.",
            },
            {
                title: "No ticket appearing? Look up",
                body:
                    "Entry machines have two ticket dispensers: a low one for cars and a high one for trucks and coaches. The machine classifies whatever it senses — and on a bike, especially with a high vehicle right behind you, it sometimes decides you're the truck and issues the ticket from the upper dispenser, at head height or above. If you're sat at the barrier and no ticket seems to be coming, look up before you hit the help button. It has confused plenty of riders for a good minute.",
            },
            {
                title: "Pull through, don't dwell",
                body:
                    "Once the barrier lifts, ride straight through and pull over well past the plaza if you need to stow the receipt or send a text. The lane behind you is moving.",
            },
        ],
    },
    {
        catnum: "05",
        chipLabel: "Keep everyone together",
        accent: "plum",
        heading: "Group rides — keep everyone together",
        items: [
            {
                title: "Each rider pays separately",
                body:
                    "Every bike takes its own ticket and pays its own toll. There's no group rate. If you're using Liber-t tags, every rider needs their own — you can't share one across bikes.",
            },
            {
                title: "Pick a meet-up after the plaza",
                body:
                    "The standard convention: ride about 500m past the péage, pull into the next lay-by, and wait. The fastest payer waits for the slowest. Don't stop within sight of the plaza — you'll block the run-off lane.",
            },
            {
                title: "Mixed payment methods",
                body:
                    "If half the group has tags and half doesn't, the tag riders should still use the green or card lanes for the first toll so the group doesn't get split. After the first plaza, you can split up — the meeting point still works.",
            },
        ],
    },
    {
        catnum: "06",
        chipLabel: "Spend less, see more",
        accent: "teal",
        heading: "Costs, alternatives, and money-saving",
        items: [
            {
                title: "Plan around the spend",
                body:
                    "A long autoroute day will cost €30–€60 in tolls on top of fuel. Budget €0.07/km as a rough average for a motorbike. Mappy and the Vinci/Sanef apps both have a toll calculator built in.",
            },
            {
                title: "Itinéraires bis",
                body:
                    "In summer, France signposts toll-free 'bis' (alternative) routes parallel to the major autoroutes. They take longer but cost nothing and often follow more interesting D-roads. Look for green 'Bison Futé' signs from June to September.",
            },
            {
                title: "Mix autoroute and D-roads",
                body:
                    "Most long tours are best done as a mix: autoroute when you just need to cover ground (e.g. Calais down to the Loire), and D-roads when the scenery starts. The autoroute is for boring bits, not the holiday.",
            },
            {
                title: "Receipts and VAT",
                body:
                    "Every payment method gives you a printed receipt on request. Vinci and Sanef both let you download itemised statements online after the fact, which is the easier path for anyone claiming business expenses.",
            },
            {
                title: "Avoid the rush",
                body:
                    "Plazas near big cities (Lille, Lyon, Marseille) back up badly on Friday afternoons in July and August. Plan to be through them by mid-morning or after 7pm. Sunday lunchtime is the calmest window for the southern routes.",
            },
        ],
    },
];

const LANE_CARDS = [
    {
        marker: "t",
        markerBg: "#e07a2e",
        markerColor: "#fff",
        title: "Orange 't' — Liber-t / Télépéage tags only",
        body:
            "Reserved for vehicles with an electronic toll tag. Do NOT enter unless your bike has one — the barrier won't open and you'll block the lane. If you don't have a tag, ignore the 't' lanes completely.",
    },
    {
        marker: "CB",
        markerBg: "#1d63b8",
        markerColor: "#fff",
        title: "Blue 'CB' — bank cards",
        body:
            "The blue CB card symbol means cards only. Insert or tap, take the receipt, gate opens. UK chip cards work in 99% of these, and contactless is rolled out almost everywhere now — saves you taking the glove off.",
    },
    {
        marker: "€",
        markerBg: "#c9a227",
        markerColor: "#3a2a0a",
        title: "Coins symbol — cash accepted",
        body:
            "A pictogram of coins and notes means the lane takes cash — either a machine that gives change or a staffed booth. The attendant is also your best bet for paper receipts or a problem ticket.",
    },
    {
        marker: "↓",
        markerBg: "#2f8f4e",
        markerColor: "#fff",
        title: "Green arrow — every payment method",
        body:
            "Tag, card, and cash all accepted. Often the longest queue on a Friday evening, but the safest pick if you're not sure. A red X over any lane means it's closed — don't join it.",
    },
];

function FrenchTollRoadsGuide() {
    usePageMeta({
        title: "How to use French toll roads on a motorbike — Planamoto",
        description:
            "A rider's guide to French autoroute tolls: how the péage system works, which lane to use, Liber-t electronic tags, the new Flux Libre barrier-less system, group ride tips, and how to save money.",
        url: "https://www.planamoto.com/guides/french-toll-roads",
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="How to use French toll roads on a motorbike"
            lede="France's autoroute network covers the whole country and is the fastest way to get anywhere — but every kilometre is tolled and the booths can catch out UK riders who've only ever ridden the M1. This is everything you need to know to roll through the péage system smoothly and without overpaying."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "French autoroute péage toll plaza",
            }}
            beforeSections={
                <>
                    <GuideStackLead
                        eyebrow="What I actually do"
                        authorNote="Spencer · Calais-to-the-Alps a few times a year"
                        heading="Three habits that make the péage a non-event"
                        intro="The whole système is below, honestly — but after a lot of Channel crossings this is all it really comes down to. Do these three and you'll never hold up a queue or overpay."
                        columns={[
                            {
                                label: "01 · Get a tag",
                                title: "Bip&Go or Fulli",
                                body: "Ordered online with a UK card. The orange lane is empty all summer — roll up, beep, barrier lifts. No glove off, ever.",
                            },
                            {
                                label: "02 · Know the lane",
                                title: "Read it by symbol",
                                body: "No tag? Blue CB for card, green arrow if unsure. Never the orange 't' lane without a tag — the barrier won't open and you block everyone.",
                            },
                            {
                                label: "03 · Flux Libre",
                                title: "Mind the camera gantries",
                                body: "The new barrier-less roads (A79, A13/14) charge your plate silently. With a tag it's automatic; without, pay online within 72 hours or it's a fine.",
                            },
                        ]}
                        backstop="Planamoto totes each day's toll estimate into the trip budget and splits it across the group — nobody's arguing over €40 of péage receipts at dinner."
                    />
                    <div className="gt-guide-transition">
                        <div className="gt-eyebrow gt-guide-transition__eyebrow">
                            The full picture
                        </div>
                        <h2 className="gt-guide-transition__heading">
                            That's my routine. Here's how the whole système works.
                        </h2>
                        <p className="gt-guide-transition__intro">
                            Everything worth knowing about the péage, grouped section by
                            section — with the bit that actually trips riders up
                            flagged, not buried.
                        </p>
                    </div>
                </>
            }
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            <GuideSection
                index={1}
                catnum={SECTIONS[0].catnum}
                heading={SECTIONS[0].heading}
                intro={SECTIONS[0].intro}
                chipLabel={SECTIONS[0].chipLabel}
                accent={ACCENT[SECTIONS[0].accent]}
                items={SECTIONS[0].items.map((it) => ({ ...it, marker: "✓" }))}
            />
            <GuideSection
                index={2}
                heading="Picking the right lane at the toll plaza"
                intro="Lane choice is where most non-French riders go wrong. The colour and symbol over each lane tells you exactly what's accepted; the consequence of picking the wrong one is a backed-up queue you can't reverse out of. The reader sits at car-driver height on the left of the lane — kerb the left side and lean over; the lanes are deliberately narrow, so don't try to pull up alongside the dispenser as you would in a car."
                highlight={{ glyph: "★", background: "var(--gt-accent)", color: "#3a2a0a" }}
                items={LANE_CARDS.map((c) => ({
                    title: c.title,
                    body: c.body,
                    marker: c.marker,
                    markerBg: c.markerBg,
                    markerColor: c.markerColor,
                }))}
            >
                <TollLaneAnimation />
            </GuideSection>
            {SECTIONS.slice(1).map((s, i) => (
                <GuideSection
                    key={s.heading}
                    index={i + 3}
                    catnum={s.catnum}
                    heading={s.heading}
                    intro={s.intro}
                    chipLabel={s.chipLabel}
                    accent={ACCENT[s.accent]}
                    items={s.items.map((it) => ({ ...it, marker: "✓" }))}
                />
            ))}

            <GuideCtaBand
                heading="We handle the plan and the budget. You handle the barrier."
                body="Route each day as a mix of autoroute and D-roads, roll the toll estimate into the trip budget, and split it across the group so nobody's counting péage receipts at dinner. Clone a hand-curated France tour as your starting point."
            />

            <RelatedGuides
                slugs={["fuel-prices", "packing-europe", "bike-security"]}
            />
            <CtaCard
                title="Planning the route?"
                body="Our hand-curated tours cover the WWII beaches in Normandy, the Alps via Stelvio, and more. Clone one as a starting point."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default FrenchTollRoadsGuide;
